import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

export default function OAuthTest() {
  const { oauthConfig, isOAuthAvailable } = useAuth();

  const getStatusIcon = (isConfigured: boolean) => {
    return isConfigured ? (
      <CheckCircle className="h-4 w-4 text-green-500" />
    ) : (
      <XCircle className="h-4 w-4 text-red-500" />
    );
  };

  const getStatusBadge = (isConfigured: boolean) => {
    return (
      <Badge variant={isConfigured ? "default" : "destructive"}>
        {isConfigured ? "Configured" : "Not Configured"}
      </Badge>
    );
  };

  const hasAnyOAuth = oauthConfig.google || oauthConfig.github || oauthConfig.microsoft;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              OAuth Configuration Test
            </CardTitle>
            <CardDescription>
              Check if OAuth providers are properly configured in your environment variables
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Overall Status */}
            <div className="p-4 border rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Overall OAuth Status</h3>
                {getStatusIcon(hasAnyOAuth)}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {hasAnyOAuth 
                  ? "At least one OAuth provider is configured" 
                  : "No OAuth providers are configured"
                }
              </p>
            </div>

            {/* Individual Providers */}
            <div className="space-y-4">
              <h3 className="font-semibold">Individual Provider Status</h3>
              
              {/* Google */}
              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  {getStatusIcon(oauthConfig.google)}
                  <div>
                    <p className="font-medium">Google OAuth</p>
                    <p className="text-sm text-muted-foreground">
                      VITE_GOOGLE_CLIENT_ID: {import.meta.env.VITE_GOOGLE_CLIENT_ID ? 'Set' : 'Not Set'}
                    </p>
                  </div>
                </div>
                {getStatusBadge(oauthConfig.google)}
              </div>

              {/* GitHub */}
              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  {getStatusIcon(oauthConfig.github)}
                  <div>
                    <p className="font-medium">GitHub OAuth</p>
                    <p className="text-sm text-muted-foreground">
                      VITE_GITHUB_CLIENT_ID: {import.meta.env.VITE_GITHUB_CLIENT_ID ? 'Set' : 'Not Set'}
                    </p>
                  </div>
                </div>
                {getStatusBadge(oauthConfig.github)}
              </div>

              {/* Microsoft */}
              <div className="flex items-center justify-between p-3 border rounded">
                <div className="flex items-center gap-3">
                  {getStatusIcon(oauthConfig.microsoft)}
                  <div>
                    <p className="font-medium">Microsoft OAuth</p>
                    <p className="text-sm text-muted-foreground">
                      VITE_MICROSOFT_CLIENT_ID: {import.meta.env.VITE_MICROSOFT_CLIENT_ID ? 'Set' : 'Not Set'}
                    </p>
                  </div>
                </div>
                {getStatusBadge(oauthConfig.microsoft)}
              </div>
            </div>

            {/* Site URL */}
            <div className="p-4 border rounded-lg bg-muted/50">
              <h3 className="font-semibold mb-2">OAuth Redirect Configuration</h3>
              <p className="text-sm">
                <strong>VITE_SITE_URL:</strong> {import.meta.env.VITE_SITE_URL || 'Not Set (using window.location.origin)'}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Current origin: {window.location.origin}
              </p>
            </div>

            {/* Instructions */}
            {!hasAnyOAuth && (
              <div className="p-4 border border-orange-200 rounded-lg bg-orange-50">
                <h3 className="font-semibold text-orange-800 mb-2">Setup Required</h3>
                <p className="text-sm text-orange-700 mb-3">
                  To enable OAuth authentication, add the following to your <code>.env.local</code> file:
                </p>
                <pre className="text-xs bg-white p-3 rounded border overflow-x-auto">
{`# OAuth Provider Client IDs
VITE_GOOGLE_CLIENT_ID=your_google_client_id_here
VITE_GITHUB_CLIENT_ID=your_github_client_id_here
VITE_MICROSOFT_CLIENT_ID=your_microsoft_client_id_here

# Site URL for OAuth redirects
VITE_SITE_URL=http://localhost:8888`}
                </pre>
                <p className="text-sm text-orange-700 mt-3">
                  See <code>scripts/OAUTH_SETUP.md</code> for detailed setup instructions.
                </p>
              </div>
            )}

            {/* Test Available Methods */}
            <div className="space-y-2">
              <h3 className="font-semibold">OAuth Method Availability</h3>
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center p-2 border rounded">
                  <p className="text-sm font-medium">Google</p>
                  <p className="text-xs text-muted-foreground">
                    {isOAuthAvailable('google') ? 'Available' : 'Unavailable'}
                  </p>
                </div>
                <div className="text-center p-2 border rounded">
                  <p className="text-sm font-medium">GitHub</p>
                  <p className="text-xs text-muted-foreground">
                    {isOAuthAvailable('github') ? 'Available' : 'Unavailable'}
                  </p>
                </div>
                <div className="text-center p-2 border rounded">
                  <p className="text-sm font-medium">Microsoft</p>
                  <p className="text-xs text-muted-foreground">
                    {isOAuthAvailable('azure') ? 'Available' : 'Unavailable'}
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 